import React, { useState } from "react";
import { LogIn, LogOut, ChevronDown, BadgeCheck } from "lucide-react";
import { COLORS } from "../constants";
import { pill, fontSerif } from "./styles";
import { useAuth } from "../../../auth/AuthContext";

/**
 * UserChip — top-right HUD element showing either:
 *   - "Sign in" pill when no user (clicking starts Emergent Google OAuth)
 *   - Avatar + name + ▼ menu (Sign out) when authenticated
 *
 * Also shows a small ✓ badge when the user holds an active Avatar Pass.
 */
export default function UserChip({ compact = false }) {
  const { user, loading, signIn, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  if (loading) return null;

  if (!user) {
    return (
      <button
        data-testid="auth-signin-button"
        onClick={signIn}
        style={{
          ...pill,
          padding: "8px 14px",
          color: COLORS.ivory,
          fontFamily: fontSerif,
          fontSize: 12,
          letterSpacing: 2,
          textTransform: "uppercase",
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <LogIn size={13} color={COLORS.gold} aria-hidden="true" />
        Sign in
      </button>
    );
  }

  const initial =
    (user.display_name || user.email || "?").trim().charAt(0).toUpperCase();

  return (
    <div data-testid="auth-user-chip" style={{ position: "relative" }}>
      <button
        data-testid="auth-user-button"
        onClick={() => setMenuOpen((v) => !v)}
        style={{
          ...pill,
          padding: "6px 12px 6px 6px",
          color: COLORS.ivory,
          fontFamily: fontSerif,
          fontSize: 12,
          letterSpacing: 1.5,
          textTransform: "none",
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          border: `1px solid ${user.hasPaid ? COLORS.gold : COLORS.navyMid}`,
        }}
      >
        {user.picture ? (
          <img
            data-testid="auth-user-avatar"
            src={user.picture}
            alt=""
            referrerPolicy="no-referrer"
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              objectFit: "cover",
              border: `1px solid ${COLORS.gold}`,
            }}
          />
        ) : (
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              background: COLORS.navyMid,
              color: COLORS.gold,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {initial}
          </span>
        )}
        {!compact && (
          <span
            data-testid="auth-user-name"
            style={{ maxWidth: 140, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {user.display_name || user.email}
          </span>
        )}
        {user.hasPaid && (
          <BadgeCheck
            size={13}
            color={COLORS.gold}
            aria-label="Avatar Pass active"
            data-testid="auth-paid-badge"
          />
        )}
        <ChevronDown size={12} color={COLORS.ivory} aria-hidden="true" />
      </button>

      {menuOpen && (
        <div
          data-testid="auth-user-menu"
          onMouseLeave={() => setMenuOpen(false)}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 200,
            background: "rgba(11,21,48,0.96)",
            border: `1px solid ${COLORS.navyMid}`,
            borderRadius: 12,
            padding: 6,
            backdropFilter: "blur(8px)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
            fontFamily: fontSerif,
            zIndex: 30,
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              fontSize: 11,
              color: "rgba(243,234,212,0.7)",
              borderBottom: `1px solid ${COLORS.navyMid}`,
              marginBottom: 4,
              wordBreak: "break-all",
            }}
          >
            {user.email}
          </div>
          <button
            data-testid="auth-signout-button"
            onClick={async () => {
              setMenuOpen(false);
              await signOut();
            }}
            style={{
              width: "100%",
              background: "transparent",
              border: "none",
              color: COLORS.ivory,
              cursor: "pointer",
              padding: "8px 12px",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: fontSerif,
              fontSize: 12,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              textAlign: "left",
            }}
          >
            <LogOut size={13} color={COLORS.gold} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
